import React, { useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  IconButton,
  Box,
  Drawer,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  ListItemIcon,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home,
  Dashboard,
  Person,
  AdminPanelSettings,
  Group,
  Store,
  Add,
  List as ListIcon,
  Logout,
  Storefront,
  PersonAdd,
  People,
  ManageAccounts,
  Receipt,
  ShoppingCart,
  Notifications,
  LocalOffer,
  Schedule,
  LocalShipping,
  Category,
  MoreHoriz,
  Error
} from '@mui/icons-material';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';
import { appConfig } from '../../config/app';

const Header = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const { state, actions } = useApp();
  const navigate = useNavigate();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    actions.logout();
    navigate('/');
    actions.showSnackbar('Logged out successfully', 'success');
  };

  const getIcon = (path) => {
    switch (path) {
      case '/':
        return <Home />;
      case '/dashboard':
        return <Dashboard />;
      case '/profile':
        return <Person />;
      case '/admin':
        return <AdminPanelSettings />;
      case '/admin/dashboard':
        return <Dashboard />;
      case '/admin/orders':
        return <Receipt />;
      case '/admin/users':
        return <ManageAccounts />;
      case '/super-admin/stores':
        return <Storefront />;
      case '/super-admin/stores/add':
        return <Add />;
      case '/super-admin/stores/list':
        return <Store />;
      case '/super-admin/users':
        return <People />;
      case '/super-admin/users/add':
        return <PersonAdd />;
      case '/super-admin/users/list':
        return <Group />;
      case '/super-admin/orders':
        return <Receipt />;
      case '/super-admin/orders/list':
        return <ShoppingCart />;
      case '/super-admin/categories/manage':
        return <Category />;
      case '/super-admin/products':
        return <ShoppingCart />;
      case '/super-admin/products/add':
        return <Add />;
      case '/super-admin/products/list':
        return <ListIcon />;
      case '/super-admin/coupons':
        return <LocalOffer />;
      case '/super-admin/coupons/add':
        return <Add />;
      case '/super-admin/coupons/list':
        return <ListIcon />;
      case '/super-admin/notifications':
        return <Notifications />;
      case '/super-admin/notifications/manage':
        return <Notifications />;
      case '/super-admin/delivery':
        return <LocalShipping />;
      case '/super-admin/delivery/time-slots':
        return <Schedule />;
      case '/super-admin/others':
        return <MoreHoriz />;
      case '/super-admin/focus-errors':
        return <Error />;
      default:
        return <Dashboard />;
    }
  };

  // Get all navigation items for mobile drawer
  const mobileNavigationItems = state.isAuthenticated 
    ? appConfig.navigation.main.filter(item => !item.public)
    : appConfig.navigation.main.filter(item => item.public);

  const mobileAdminItems = state.isAuthenticated && (state.user?.role === 'admin' || state.user?.role === 'store_admin')
    ? appConfig.navigation.admin 
    : [];

  const mobileSuperAdminItems = state.isAuthenticated && (state.user?.role === 'super_admin' || state.user?.role === 'super-admin')
    ? appConfig.navigation.superAdmin 
    : [];

  const renderMobileNavigationItems = () => (
    <>
      {/* Main Navigation */}
      {mobileNavigationItems.map((item) => (
        <ListItem key={item.path} disablePadding>
          <ListItemButton
            component={Link}
            to={item.path}
            onClick={() => setMobileOpen(false)}
          >
            <ListItemIcon sx={{ minWidth: 36 }}>
              {getIcon(item.path)}
            </ListItemIcon>
            <ListItemText 
              primary={item.title}
              primaryTypographyProps={{
                fontSize: '14px'
              }}
            />
          </ListItemButton>
        </ListItem>
      ))}
      
      {/* Admin Section */}
      {mobileAdminItems.length > 0 && (
        <>
          <ListItem>
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '12px' }}>
              {state.user?.role === 'store_admin' ? 'Store Admin' : 'Admin'}
            </Typography>
          </ListItem>
          {mobileAdminItems.map((item) => (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                onClick={() => setMobileOpen(false)}
                sx={{ pl: 2 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  {getIcon(item.path)}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '13px'
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </>
      )}

      {/* Super Admin Section */}
      {mobileSuperAdminItems.length > 0 && (
        <>
          <ListItem>
            <Typography variant="overline" color="text.secondary" sx={{ fontSize: '12px' }}>
              Super Admin
            </Typography>
          </ListItem>
          {mobileSuperAdminItems.map((item) => (
            <React.Fragment key={item.path}>
                             <ListItem disablePadding>
                 <ListItemButton
                   component={item.children ? 'div' : Link}
                   to={item.children ? undefined : item.path}
                   onClick={item.children ? undefined : () => setMobileOpen(false)}
                   sx={{ pl: 2 }}
                 >
                   <ListItemIcon sx={{ minWidth: 36 }}>
                     {getIcon(item.path)}
                   </ListItemIcon>
                   <ListItemText 
                     primary={item.title}
                     primaryTypographyProps={{
                       fontSize: '13px',
                       fontWeight: item.children ? 'bold' : 'normal'
                     }}
                   />
                 </ListItemButton>
               </ListItem>
              {/* Sub-items for collapsible menus */}
              {item.children && item.children.map((child) => (
                                 <ListItem key={child.path} disablePadding>
                   <ListItemButton
                     component={Link}
                     to={child.path}
                     onClick={() => setMobileOpen(false)}
                     sx={{ pl: 4 }}
                   >
                     <ListItemIcon sx={{ minWidth: 36 }}>
                       {getIcon(child.path)}
                     </ListItemIcon>
                     <ListItemText 
                       primary={child.title}
                       primaryTypographyProps={{
                         fontSize: '12px'
                       }}
                     />
                   </ListItemButton>
                 </ListItem>
              ))}
            </React.Fragment>
          ))}
        </>
      )}
      
             {/* Authentication Actions */}
       {state.isAuthenticated && (
         <ListItem disablePadding>
           <ListItemButton onClick={handleLogout}>
             <ListItemIcon sx={{ minWidth: 36 }}>
               <Logout />
             </ListItemIcon>
             <ListItemText 
               primary="Logout"
               primaryTypographyProps={{
                 fontSize: '14px'
               }}
             />
           </ListItemButton>
         </ListItem>
       )}

       {!state.isAuthenticated && (
         <ListItem disablePadding>
           <ListItemButton
             component={Link}
             to="/auth"
             onClick={() => setMobileOpen(false)}
           >
             <ListItemIcon sx={{ minWidth: 36 }}>
               <Person />
             </ListItemIcon>
             <ListItemText 
               primary="Login"
               primaryTypographyProps={{
                 fontSize: '14px'
               }}
             />
           </ListItemButton>
         </ListItem>
       )}
    </>
  );

  const drawer = (
    <Box sx={{ width: 250 }} role="presentation">
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" component="div">
          {appConfig.name}
        </Typography>
      </Box>
      <List>
        {renderMobileNavigationItems()}
      </List>
    </Box>
  );

  return (
    <>
      <AppBar position="sticky" elevation={2}>
        <Toolbar>
          {/* Mobile menu button */}
          {isMobile && state.isAuthenticated && (
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Logo */}
          <Typography
            variant="h6"
            component={Link}
            to="/"
            sx={{
              flexGrow: 1,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 'bold'
            }}
          >
            {appConfig.name}
          </Typography>

          {/* Desktop navigation - removed since we now have sidebar */}
        </Toolbar>
      </AppBar>

      {/* Mobile drawer */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
        sx={{
          display: { xs: 'block', md: 'none' },
          '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 250 },
        }}
      >
        {drawer}
      </Drawer>
    </>
  );
};

export default Header; 